import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { reduxForm, Field } from 'redux-form';

import {
  Button, Typography, Grid, Box, Paper, ThemeProvider, createTheme,
  Radio, RadioGroup, FormControlLabel, Chip, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Checkbox, Stack, Tabs, Tab,
  Tooltip, IconButton
} from '@mui/material';

import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Preview as PreviewIcon,
  LocalShippingRounded as ShippingIcon,
  Search as SearchIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import {
  HUB_MANAGER_ROLE_ID, HUB_OPERATOR_ROLE_ID, MERCHANT_ROLE_ID,
  OPERATION_TYPE_FOR_PICKUP_READY, OPERATION_TYPE_FOR_GOODS_RECEIVED,
  OPERATION_TYPE_FOR_PICKUP_ASSIGNED, OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED,
  // OPERATION_TYPE_FOR_PICKUP_WITHDRWN,
  OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION,
  OPERATION_TYPE_FOR_GOODS_RECEIVED_RS,
  OPERATION_TYPE_FOR_RE_RESCHEDULE,
  OPERATION_TYPE_FOR_PICK_UP_ORDER_CREATION,
} from '../../../../../../utils/pnd/PndConstants';

import PndRestApi from 'src/utils/pnd/PndRestApi';
import { formatDate } from 'src/utils/DateUtils';
import { showNotification } from 'src/shared/components/Notification';
import renderRadioButtonField from 'src/shared/components/form/RadioButton';
import AllOrdersMapModal from './AllOrdersMapModal';

// --- Theme Configuration ---
const theme = createTheme({
  typography: {
    fontFamily: 'ProductSansLight, sans-serif',
    h3: { fontFamily: 'ProductSansBold, sans-serif', fontSize: 24, fontWeight: 600 },
    h5: { fontFamily: 'ProductSansBold, sans-serif', fontWeight: 700 },
    subtitle1: { fontSize: 14, color: '#64748b' },
    h6: { fontSize: 16, fontWeight: 600, color: '#1e293b' },
    subtitle2: { fontSize: 13, fontWeight: 600, color: '#475569' }
  },
  palette: {
    primary: { main: '#0ea5e9' },
    success: { main: '#10b981' }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }
      }
    }
  }
});

// --- Label Helper ---
// const StatusBadge = ({ type, statusId }) => {
//   const isReady = (type === 3 && statusId === OPERATION_TYPE_FOR_PICKUP_READY) ||
//     (type === 1 && statusId === OPERATION_TYPE_FOR_GOODS_RECEIVED);
//   const isAssigned = statusId === OPERATION_TYPE_FOR_PICKUP_ASSIGNED ||
//     statusId === OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED;

//   if (isReady) return (
//     <Chip label="Ready" size="small" color="success" variant="outlined"
//       sx={{ fontSize: '9px', height: '18px', fontWeight: 700, borderStyle: 'dashed', animation: 'pulse 2s infinite' }}
//     />
//   );

//   if (isAssigned) return (
//     <Chip label="Assigned" size="small" color="primary" variant="outlined"
//       sx={{ fontSize: '9px', height: '18px', fontWeight: 700 }}
//     />
//   );

//   return (
//     <Chip label="Pending" size="small" sx={{ fontSize: '9px', height: '18px', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
//   );
// };

const OrderDetailsView = (props) => {
  const {
    reactTableData, selectedOrderIds, handleCheckboxClick, handleSelectAll,
    getOrderTypeFilterType, edit, showDialogBox, processOrder, viewDetails,
    openMap, openMapPickUp, onBulkAssignClick, onBulkGoodsReceivedClick,
    setSelectedOrderIds, refresh
  } = props;

  const orderPreferences = useSelector(state => state.orderPreferences);
  const userRole = localStorage.getItem("uri");

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderItemDetails, setOrderItemDetails] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArea, setSelectedArea] = useState('All Areas');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('All Time Slots');
  const [selectedSapType, setSelectedSapType] = useState('All SAP Types');
  const [activeTab, setActiveTab] = useState('PGR');
  const [showAllOrdersMap, setShowAllOrdersMap] = useState(false);
  const [isRedirect, setRedirect] = useState(false);

  const rows = reactTableData?.tableRowsData || [];
  const selectedOrder = useMemo(() => rows.find(r => r.orderId === selectedOrderId), [rows, selectedOrderId]);

  // Extract unique delivery areas for the dropdown filter
  const uniqueAreas = useMemo(() => {
    const areas = rows.map(r => r.areaName).filter(Boolean);
    return ['All Areas', ...Array.from(new Set(areas))];
  }, [rows]);

  // Extract unique time slots for the dropdown filter
  const uniqueTimeSlots = useMemo(() => {
    const slots = rows.map(r => r.reqTimeSlot).filter(Boolean);
    return ['All Time Slots', ...Array.from(new Set(slots))];
  }, [rows]);

  // Extract unique SAP Order Types for the dropdown filter
  const uniqueSapTypes = useMemo(() => {
    const types = rows.map(r => r.sapOrderType).filter(Boolean);
    return ['All SAP Types', ...Array.from(new Set(types))];
  }, [rows]);



  const getSapTypeLabel = (type) => {
    if (type === 'ZEOR') return 'Standard (ZEOR)';
    if (type === 'ZECC') return 'Yalla (ZECC)';
    return type;
  };

  const filteredOrders = useMemo(() => {
    return rows.filter(order => {
      // 1. Text Search matching order number, erp number, area name, time slot, status, or SAP type
      const matchesSearch =
        order.orderNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.erpOrderNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.areaName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.reqTimeSlot?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.orderStatus?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.sapOrderType?.toLowerCase().includes(searchQuery.toLowerCase());

      // 2. Dropdown matching specific delivery area
      const matchesArea = selectedArea === 'All Areas' || order.areaName === selectedArea;

      // 3. Dropdown matching specific time slot
      const matchesTimeSlot = selectedTimeSlot === 'All Time Slots' || order.reqTimeSlot === selectedTimeSlot;

      // 4. Dropdown matching specific SAP Order Type
      const matchesSapType = selectedSapType === 'All SAP Types' || order.sapOrderType === selectedSapType;

      // 5. Dropdown matching specific Order Status via activeTab
      let matchesStatus = false;
      if (activeTab === 'PGR') {
        matchesStatus =
          Number(order.orderStatusId) === OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION ||
          Number(order.orderStatusId) === OPERATION_TYPE_FOR_PICK_UP_ORDER_CREATION ||
          Number(order.orderStatusId) === 6;
      } else if (activeTab === 'ASSIGN') {
        matchesStatus =
          Number(order.orderStatusId) === OPERATION_TYPE_FOR_GOODS_RECEIVED ||
          Number(order.orderStatusId) === OPERATION_TYPE_FOR_GOODS_RECEIVED_RS ||
          Number(order.orderStatusId) === OPERATION_TYPE_FOR_PICKUP_READY ||
          Number(order.orderStatusId) === 19;
      }

      return matchesSearch && matchesArea && matchesTimeSlot && matchesSapType && matchesStatus;
    });
  }, [rows, searchQuery, selectedArea, selectedTimeSlot, selectedSapType, activeTab]);

  const eligibleFilteredOrders = useMemo(() => {
    return filteredOrders.filter(order =>
      (Number(order.orderType) === 1 && Number(order.orderStatusId) === OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION) ||
      (Number(order.orderType) === 1 && (Number(order.orderStatusId) === OPERATION_TYPE_FOR_GOODS_RECEIVED || Number(order.orderStatusId) === OPERATION_TYPE_FOR_GOODS_RECEIVED_RS)) ||
      ((Number(order.orderType) === 3 || Number(order.orderType) === 2) && (Number(order.orderStatusId) === OPERATION_TYPE_FOR_PICKUP_READY || Number(order.orderStatusId) === 19))
    );
  }, [filteredOrders]);

  useEffect(() => {
    if (setSelectedOrderIds) {
      setSelectedOrderIds([]);
    }
    setSelectedOrderId(null);
  }, [activeTab, setSelectedOrderIds]);

  useEffect(() => {
    if (userRole == MERCHANT_ROLE_ID && activeTab !== 'PGR') {
      setActiveTab('PGR');
    }
  }, [userRole, activeTab]);

  useEffect(() => {
    if (selectedOrderId) {
      const fetchItems = async () => {
        try {
          const res = await PndRestApi.GetOrderItemWiseDetails(selectedOrderId);
          if (res.status === 200 && res.data.status === 200) setOrderItemDetails(res.data.data || []);
        } catch (e) { console.error("Item Fetch Error:", e); }
      };
      fetchItems();
    }
  }, [selectedOrderId]);

  const enabledOptions = useMemo(() => {
    const p = orderPreferences?.data || {};
    const opts = [
      { label: 'Drop Off Only', value: 1, enabled: p.dropOffEnabled },
      { label: 'Pick Up Only', value: 2, enabled: p.pickupEnabled },
      { label: 'Pick and Drop', value: 3, enabled: p.pickAndDropEnabled }
    ].filter(o => o.enabled);
    if (opts.length > 1) opts.unshift({ label: 'All Cases', value: 0 });
    return opts;
  }, [orderPreferences]);

  if (isRedirect) return <Redirect to="/web_app/pnd_order/order_add" />;

  const handleTypeChange = (v) => {
    getOrderTypeFilterType({ value: v });
    setSelectedOrderId(null);
    setSelectedArea('All Areas');
    setSelectedTimeSlot('All Time Slots');
    setSelectedSapType('All SAP Types');
    setActiveTab('PGR');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedArea('All Areas');
    setSelectedTimeSlot('All Time Slots');
    setSelectedSapType('All SAP Types');
    setActiveTab('PGR');
    setSelectedOrderId(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Stack>
            <Typography variant="h3">Order Management</Typography>
            <Typography variant="subtitle1">Manage and track your delivery lifecycle in real-time.</Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            {orderPreferences?.data?.addNewOrderEnabled && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRedirect(true)}
                sx={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)', color: 'white' }}>
                New Order
              </Button>
            )}
            <Tooltip title="Refresh Details">
              <IconButton
                onClick={refresh}
                sx={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  color: '#0284c7',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  height: '40px',
                  width: '40px',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {(userRole == HUB_MANAGER_ROLE_ID || userRole == HUB_OPERATOR_ROLE_ID) && (
              <>
                {activeTab === 'ASSIGN' && selectedOrderIds.length > 0 && (
                  <Button variant="contained" startIcon={<AssignmentIcon />} color="success"
                    onClick={() => {
                      const hasMissingSchedule = selectedOrderIds.some(s => {
                        const ord = rows.find(r => r.orderId === s.id);
                        return ord && !ord.scheduledDateTimeSlot;
                      });
                      if (hasMissingSchedule) {
                        showNotification("theme-light", "ltr", "Error", "Please fill Scheduled Delivery date/time for all selected orders before assigning.");
                        return;
                      }
                      onBulkAssignClick();
                    }}>
                    Bulk Assign
                  </Button>
                )}

                {activeTab === 'PGR' && selectedOrderIds.some(s => {
                  const ord = rows.find(r => r.orderId === s.id);
                  return ord && ord.orderType === 1 && ord.orderStatusId === OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION;
                }) && (
                    <Button variant="contained" startIcon={<ShippingIcon />} color="primary"
                      onClick={onBulkGoodsReceivedClick}>
                      Bulk Goods Received
                    </Button>
                  )}
              </>
            )}
          </Stack>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 4, minHeight: '740px', display: 'flex', flexDirection: 'column' }}>
              {selectedOrder ? (
                <Box>
                  <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h5">Order #{selectedOrder.orderNumber}</Typography>
                        {selectedOrder.customerTagName &&
                          <Box
                            component="span"
                            sx={{
                              backgroundColor: selectedOrder?.customerTagColorCode || '#32CA71',
                              color: 'white',
                              fontSize: '0.75rem',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '12px',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                              fontWeight: 700,
                            }}
                          >
                            {selectedOrder.customerTagName}
                          </Box>
                        }
                      </Stack>
                      {selectedOrder.erpOrderNo && <Typography variant="h5">ERP #{selectedOrder.erpOrderNo} </Typography>}
                      <Stack direction="row" spacing={1}>
                        <Chip label={selectedOrder.orderStatus} size="small" color="primary" variant="outlined" />
                        <Chip label={selectedOrder.orderPriorityName} size="small" sx={{ bgcolor: selectedOrder.orderPriorityId === 1 ? '#fee2e2' : '#fef3c7', color: '#991b1b' }} />
                        {selectedOrder.reqTimeSlot && <Chip label={selectedOrder.reqTimeSlot} size="small" variant="outlined" />}
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => edit(selectedOrder)}
                        sx={{ px: 1.5, py: 0.5, fontSize: '12px', minWidth: 'auto', height: '32px' }}>
                        Edit
                      </Button>
                      <Button variant="outlined" startIcon={<PreviewIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => viewDetails(selectedOrder)}
                        sx={{ px: 1.5, py: 0.5, fontSize: '12px', minWidth: 'auto', height: '32px' }}>
                        Preview
                      </Button>
                      {/* <Button variant="outlined" color="warning" startIcon={<AssignmentIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => showDialogBox(selectedOrder, OPERATION_TYPE_FOR_RE_RESCHEDULE)}
                        sx={{ px: 1.5, py: 0.5, fontSize: '12px', minWidth: 'auto', height: '32px' }}>
                        Reschedule
                      </Button> */}
                    </Stack>
                  </Box>

                  <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd', display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ m: 0, color: '#0369a1' }}>Operational Actions:</Typography>
                  {userRole == MERCHANT_ROLE_ID && (selectedOrder.orderType == 2 || selectedOrder.orderType == 3) && (selectedOrder.orderStatusId == OPERATION_TYPE_FOR_PICK_UP_ORDER_CREATION || selectedOrder.orderStatusId == 6) && (
                      <Button size="small" variant="contained" color="info" startIcon={<ShippingIcon />} onClick={() => showDialogBox(selectedOrder, OPERATION_TYPE_FOR_PICKUP_READY)}>Pick Up Ready</Button>
                    )}
                    {userRole != MERCHANT_ROLE_ID && selectedOrder.orderType == 1 && selectedOrder.orderStatusId == OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION && (
                      <Button size="small" variant="contained" color="success" startIcon={<ShippingIcon />} onClick={() => showDialogBox(selectedOrder, OPERATION_TYPE_FOR_GOODS_RECEIVED)}>Goods Received</Button>
                    )}
                    {((userRole == HUB_OPERATOR_ROLE_ID || userRole == HUB_MANAGER_ROLE_ID) &&
                      (((selectedOrder.orderType == 3 || selectedOrder.orderType == 2) && (selectedOrder.orderStatusId == OPERATION_TYPE_FOR_PICKUP_READY || selectedOrder.orderStatusId == 19)) ||
                        (selectedOrder.orderType == 1 && (selectedOrder.orderStatusId == OPERATION_TYPE_FOR_GOODS_RECEIVED || selectedOrder.orderStatusId == OPERATION_TYPE_FOR_GOODS_RECEIVED_RS)))
                    ) && (
                        <Button variant="contained" sx={{ bgcolor: '#FFC107', color: '#000' }} startIcon={<AssignmentIcon />}
                          onClick={() => {
                            if (!selectedOrder.scheduledDateTimeSlot) {
                              showNotification("theme-light", "ltr", "Error", "Please fill Scheduled Delivery date/time before assigning order.");
                              return;
                            }
                            processOrder(selectedOrder, selectedOrder.orderType == 1 ? OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED : OPERATION_TYPE_FOR_PICKUP_ASSIGNED);
                          }}>
                          Assign Order
                        </Button>
                      )}
                  </Box>

                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="subtitle2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} /> PICKUP
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.fromName}</Typography>
                          <Typography variant="body2" sx={{ color: 'primary.main', cursor: 'pointer' }} onClick={() => openMapPickUp(selectedOrder)}>
                            {selectedOrder.pickupAddress || 'No address'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} /> DELIVERY
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.toName}</Typography>
                          <Typography variant="body2" sx={{ color: 'primary.main', cursor: 'pointer' }} onClick={() => openMap(selectedOrder)}>
                            {selectedOrder.dropOffAddress || 'No address'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2">Execution Timeline</Typography>
                        <Stack spacing={1.5} mt={2}>
                          {[{ l: 'Requested', v: selectedOrder.requestedDateTimeSlot }, { l: 'Scheduled', v: selectedOrder.scheduledDateTimeSlot }, { l: 'Completed', v: selectedOrder.completedDateTime }].map((it, idx) => (
                            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">{it.l}:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.v ? formatDate(it.v) : '--'}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" mb={2}>Item Details ({orderItemDetails.length})</Typography>
                    <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                          <TableRow>
                            {['Sl', 'Barcode', 'Qty', 'Weight'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderItemDetails.map((item, i) => (
                            <TableRow key={i}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>{item.boxBarCode}</TableCell>
                              <TableCell>{item.noOfItems}</TableCell>
                              <TableCell>{item.weightKg ? `${item.weightKg} Kg` : '--'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ m: 'auto', textAlign: 'center' }}>
                  <Typography variant="h6" color="text.disabled">Select an order from the list</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, height: '740px', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, val) => setActiveTab(val)}
                  variant="fullWidth"
                  textColor="primary"
                  indicatorColor="primary"
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      textTransform: 'none',
                      color: '#64748b',
                      '&.Mui-selected': {
                        color: '#3b82f6',
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#3b82f6',
                      height: '3px',
                      borderRadius: '3px'
                    }
                  }}
                >
                  <Tab label="PGR/PUR" value="PGR" />
                  {userRole != MERCHANT_ROLE_ID && <Tab label="ASSIGN" value="ASSIGN" />}
                </Tabs>
              </Box>

              {/* <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <FilterListIcon color="primary" />
                <Typography variant="h6">Control Panel</Typography>
              </Stack> */}

              <Box mb={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">Filters</Typography>
                    {(searchQuery || selectedArea !== 'All Areas' || selectedTimeSlot !== 'All Time Slots' || selectedSapType !== 'All SAP Types' || activeTab !== 'PGR') && (
                      <Button size="small" variant="text" color="primary" onClick={handleClearFilters} sx={{ textTransform: 'none', py: 0, px: 1, fontSize: '11px', minWidth: 0 }}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                  <TextField size="small" placeholder="Search..." variant="standard"
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 1, color: 'text.disabled' }} /> }}
                  />
                </Stack>

                {/* Delivery Area and Time Slot Dropdown Filters */}
                <Stack direction="row" spacing={1.5} mb={1.5}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedArea}
                    onChange={e => setSelectedArea(e.target.value)}
                    SelectProps={{ native: true }}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff'
                      }
                    }}
                  >
                    {uniqueAreas.map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedTimeSlot}
                    onChange={e => setSelectedTimeSlot(e.target.value)}
                    SelectProps={{ native: true }}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff'
                      }
                    }}
                  >
                    {uniqueTimeSlots.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </TextField>
                </Stack>



                <Stack>
                  {enabledOptions.map(opt => (
                    <Field key={opt.value} name="orderType" component={renderRadioButtonField} label={opt.label} radioValue={opt.value} onChange={handleTypeChange} />
                  ))}
                </Stack>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Stack direction="row" alignItems="center" justifyContent="space-between" px={1} mb={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {userRole != MERCHANT_ROLE_ID && (
                    <Checkbox
                      size="small"
                      checked={eligibleFilteredOrders.length > 0 && eligibleFilteredOrders.every(fo => selectedOrderIds.some(s => s.id === fo.orderId))}
                      indeterminate={eligibleFilteredOrders.length > 0 && selectedOrderIds.some(s => eligibleFilteredOrders.some(fo => fo.orderId === s.id)) && !eligibleFilteredOrders.every(fo => selectedOrderIds.some(s => s.id === fo.orderId))}
                      onChange={() => {
                        handleSelectAll(eligibleFilteredOrders);
                        setSelectedOrderId(null);
                      }}
                    />
                  )}
                  <Typography variant="subtitle2">Orders ({filteredOrders.length})</Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MapIcon />}
                  onClick={() => setShowAllOrdersMap(true)}
                  disabled={filteredOrders.length === 0}
                  sx={{ borderRadius: '8px', textTransform: 'none', py: 0.25 }}
                >
                  Map View
                </Button>
              </Stack>

              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <RadioGroup
                  value={selectedOrderId}
                  onChange={e => {
                    setSelectedOrderId(Number(e.target.value));
                    if (setSelectedOrderIds) {
                      setSelectedOrderIds([]);
                    }
                  }}
                >
                  {filteredOrders.map(order => (
                    <Box key={order.orderId} sx={{
                      p: 1, mb: 1, borderRadius: '12px', border: '1px solid',
                      borderColor: selectedOrderId === order.orderId ? 'primary.light' : 'transparent',
                      bgcolor: selectedOrderId === order.orderId ? '#f0f9ff' : 'transparent',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}>
                      <Stack direction="row" alignItems="center">
                        {userRole != MERCHANT_ROLE_ID && (((order.orderType == 3 || order.orderType == 2) && (order.orderStatusId == OPERATION_TYPE_FOR_PICKUP_READY || order.orderStatusId == 19)) ||
                          (order.orderType == 1 && (order.orderStatusId == OPERATION_TYPE_FOR_GOODS_RECEIVED || order.orderStatusId == OPERATION_TYPE_FOR_GOODS_RECEIVED_RS)) ||
                          (order.orderType == 1 && order.orderStatusId == OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION)) && (
                            <Checkbox
                              size="small"
                              checked={selectedOrderIds.some(s => s.id === order.orderId)}
                              onChange={() => {
                                handleCheckboxClick(order);
                                setSelectedOrderId(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        <FormControlLabel value={order.orderId} control={<Radio size="small" />} label={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <Stack sx={{ overflow: 'hidden' }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {order.orderNumber} {order?.erpOrderNo ? "| " + order.erpOrderNo : ""}
                                <Box component="span" sx={{ color: '#3b82f6', ml: 0.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                  onClick={(e) => { e.stopPropagation(); openMap(order); }}>
                                  | {order.areaName}  | {order?.reqTimeSlot}
                                </Box>


                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                {order.customerTagName && (
                                  <Box
                                    component="span"
                                    sx={{
                                      backgroundColor: order?.customerTagColorCode || '#32CA71',
                                      color: 'white',
                                      fontSize: '0.65rem',
                                      px: 1,
                                      py: 0.2,
                                      borderRadius: '10px',
                                      ml: 0.5,
                                      display: 'inline-block',
                                      verticalAlign: 'middle',
                                      fontWeight: 700,
                                      cursor: 'pointer'
                                    }}
                                    onClick={(e) => { e.stopPropagation(); openMap(order); }}
                                  >
                                    {order.customerTagName}
                                  </Box>
                                )}
                                {order.originalOrderNo && (
                                  <Box
                                    component="span"
                                    sx={{
                                      backgroundColor: '#ff4b57',
                                      color: 'white',
                                      fontSize: '0.65rem',
                                      px: 1,
                                      py: 0.2,
                                      borderRadius: '10px',
                                      ml: 0.5,
                                      display: 'inline-block',
                                      verticalAlign: 'middle',
                                      fontWeight: 700,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    RETURN - {order.originalOrderNo}
                                  </Box>
                                )}

                                <Typography variant="caption" noWrap>{order.fromName} → {order.toName}</Typography>
                                {order.distance && (
                                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                    • {order.distance}
                                  </Typography>
                                )}
                                {order.ttp && (
                                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                                    • {order.ttp} min
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                            {/* <StatusBadge type={order.orderType} statusId={order.orderStatusId} /> */}
                          </Box>
                        } sx={{ width: '100%', m: 0, '& .MuiFormControlLabel-label': { flexGrow: 1, width: '100%' } }} />
                      </Stack>
                    </Box>
                  ))}
                </RadioGroup>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <AllOrdersMapModal
        show={showAllOrdersMap}
        toggle={() => setShowAllOrdersMap(false)}
        orders={filteredOrders}
      />
    </ThemeProvider>
  );
};

OrderDetailsView.propTypes = {
  reactTableData: PropTypes.object.isRequired,
  selectedOrderIds: PropTypes.array,
  getOrderTypeFilterType: PropTypes.func,
  edit: PropTypes.func,
  showDialogBox: PropTypes.func,
  processOrder: PropTypes.func,
  viewDetails: PropTypes.func,
  handleCheckboxClick: PropTypes.func,
  handleSelectAll: PropTypes.func,
  openMap: PropTypes.func,
  openMapPickUp: PropTypes.func,
  onBulkAssignClick: PropTypes.func,
  onBulkGoodsReceivedClick: PropTypes.func,
  setSelectedOrderIds: PropTypes.func,
};

const mapStateToProps = (state) => {
  const p = state.orderPreferences?.data || {};
  const opts = [
    { v: 1, e: p.dropOffEnabled }, { v: 2, e: p.pickupEnabled }, { v: 3, e: p.pickAndDropEnabled }
  ].filter(o => o.e);
  return { initialValues: { orderType: opts.length === 1 ? opts[0].v : 0 } };
};

export default connect(mapStateToProps)(reduxForm({ form: "OrderDetailsViewForm", enableReinitialize: true })(OrderDetailsView));